import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Search, RotateCcw, IndianRupee, Calendar, FileText,
  Plus, X, Package, AlertCircle, Banknote, Smartphone, CreditCard, Building2
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const REFUND_MODE_STYLES = {
  CASH: 'bg-emerald-100 text-emerald-700',
  UPI: 'bg-purple-100 text-purple-700',
  CARD: 'bg-blue-100 text-blue-700',
  BANK: 'bg-slate-100 text-slate-700',
};

const getRefundModeBadge = (mode) => {
  const style = REFUND_MODE_STYLES[mode] || 'bg-slate-100 text-slate-700';
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${style}`}>{mode || 'CASH'}</span>;
};

const POSRefundsPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const { isOwner, canCreate } = usePermissions();
  const canCreateRefund = isOwner || canCreate('pos');
  const [refunds, setRefunds] = useState([]);
  const [stats, setStats] = useState({ total_refunds: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [showNewRefund, setShowNewRefund] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [refundItems, setRefundItems] = useState([]);
  const [refundReason, setRefundReason] = useState('');
  const [refundMode, setRefundMode] = useState('CASH');
  const [processing, setProcessing] = useState(false);

  const REFUND_MODES = [
    { value: 'CASH', label: 'Cash', icon: Banknote, color: 'emerald' },
    { value: 'UPI', label: 'UPI', icon: Smartphone, color: 'purple' },
    { value: 'CARD', label: 'Card', icon: CreditCard, color: 'blue' },
    { value: 'BANK', label: 'Bank', icon: Building2, color: 'slate' }
  ];

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) fetchRefunds();
  }, [bid]);

  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pos/refunds');
      setRefunds(data.refunds || []);
      if (data.stats) setStats(data.stats);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load refunds'));
    } finally {
      setLoading(false);
    }
  };

  const searchInvoice = async () => {
    if (!invoiceSearch.trim()) return;
    try {
      const { data } = await api.get('/pos/invoices', { params: { search: invoiceSearch } });
      if (data.invoices?.length > 0) {
        const inv = data.invoices[0];
        setSelectedInvoice(inv);
        setRefundItems(inv.items.map(item => ({ ...item, refund_qty: 0, selected: false })));
      } else {
        toast.error('Invoice not found');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error searching invoice'));
    }
  };

  const handleRefund = async () => {
    const itemsToRefund = refundItems.filter(item => item.selected && item.refund_qty > 0);
    if (itemsToRefund.length === 0) {
      toast.error('Select items to refund');
      return;
    }
    if (!refundReason.trim()) {
      toast.error('Enter refund reason');
      return;
    }

    setProcessing(true);
    try {
      const { data } = await api.post('/pos/refund', {
        invoice_id: selectedInvoice.id,
        items: itemsToRefund.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          quantity: item.refund_qty,
          price: item.price,
          amount: item.refund_qty * item.price
        })),
        refund_amount: refundTotal,
        reason: refundReason,
        refund_mode: refundMode,
      });
      toast.success(`Refund ${data.refund?.refund_number} created`);
      setShowNewRefund(false);
      setSelectedInvoice(null);
      setRefundItems([]);
      setRefundReason('');
      setRefundMode('CASH');
      setInvoiceSearch('');
      fetchRefunds();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error creating refund'));
    } finally {
      setProcessing(false);
    }
  };

  const refundTotal = refundItems.filter(i => i.selected).reduce((sum, i) => sum + (i.refund_qty * i.price), 0);

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/business/${bid}/pos/bills`)} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Refunds & Returns</h1>
            <p className="text-sm text-slate-500">Process returns and refunds</p>
          </div>
        </div>
        {canCreateRefund && (
          <button
            onClick={() => setShowNewRefund(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> New Refund
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <RotateCcw className="w-4 h-4 text-red-500" />
            <span className="text-xs text-slate-500">Total Refunds</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{stats.total_refunds || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <IndianRupee className="w-4 h-4 text-red-500" />
            <span className="text-xs text-slate-500">Total Amount</span>
          </div>
          <p className="text-2xl font-bold text-red-600">₹{(stats.total_amount || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Refunds List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800">Recent Refunds</h2>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : refunds.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No refunds yet</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {refunds.map((refund, i) => (
              <div key={i} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-800">{refund.refund_number}</p>
                    <p className="text-sm text-slate-500">Original: {refund.original_invoice_number}</p>
                    <p className="text-xs text-slate-400">{refund.created_at?.split('T')[0]}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-600">-₹{refund.refund_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                    <div className="flex items-center justify-end gap-1 mt-0.5">
                      {getRefundModeBadge(refund.refund_mode)}
                      <p className="text-xs text-slate-500">{refund.items?.length || 0} items</p>
                    </div>
                  </div>
                </div>
                {refund.reason && (
                  <p className="mt-2 text-sm text-slate-600 bg-slate-50 px-3 py-1.5 rounded">{refund.reason}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Refund Modal */}
      {showNewRefund && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-800">Create Refund</h3>
              <button onClick={() => { setShowNewRefund(false); setSelectedInvoice(null); }} className="p-1.5 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Invoice Search */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchInvoice()}
                  placeholder="Enter invoice number (e.g., POS-00001)"
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm"
                />
                <button onClick={searchInvoice} className="px-4 py-2.5 bg-slate-800 text-white rounded-lg">
                  <Search className="w-4 h-4" />
                </button>
              </div>

              {/* Selected Invoice */}
              {selectedInvoice && (
                <>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{selectedInvoice.invoice_number}</p>
                        <p className="text-sm text-slate-500">{selectedInvoice.customer_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-800">₹{selectedInvoice.grand_total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-slate-500">{selectedInvoice.invoice_date}</p>
                      </div>
                    </div>
                  </div>

                  {/* Items Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Select items to refund:</label>
                    {refundItems.map((item, i) => (
                      <div key={i} className={`p-3 border rounded-lg ${item.selected ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={item.selected}
                            onChange={(e) => {
                              const newItems = [...refundItems];
                              newItems[i].selected = e.target.checked;
                              if (e.target.checked && newItems[i].refund_qty === 0) {
                                newItems[i].refund_qty = item.quantity;
                              }
                              setRefundItems(newItems);
                            }}
                            className="w-4 h-4 rounded"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-slate-700">{item.item_name}</p>
                            <p className="text-xs text-slate-500">₹{item.price} × {item.quantity}</p>
                          </div>
                          {item.selected && (
                            <input
                              type="number"
                              min="1"
                              max={item.quantity}
                              value={item.refund_qty}
                              onChange={(e) => {
                                const newItems = [...refundItems];
                                newItems[i].refund_qty = Math.min(parseInt(e.target.value) || 0, item.quantity);
                                setRefundItems(newItems);
                              }}
                              className="w-16 px-2 py-1 border rounded text-center text-sm"
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="text-sm font-medium text-slate-700">Reason for refund:</label>
                    <textarea
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Enter reason for refund..."
                      className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm"
                      rows={2}
                    />
                  </div>

                  {/* Refund Mode */}
                  <div>
                    <label className="text-sm font-medium text-slate-700">Refund Mode:</label>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {REFUND_MODES.map(mode => {
                        const ModeIcon = mode.icon;
                        const isActive = refundMode === mode.value;
                        return (
                          <button
                            key={mode.value}
                            type="button"
                            onClick={() => setRefundMode(mode.value)}
                            className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border-2 transition-all ${
                              isActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <ModeIcon className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                            <span className={`text-[11px] font-medium ${isActive ? 'text-blue-700' : 'text-slate-500'}`}>{mode.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Refund Summary */}
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-red-800">Refund Amount</span>
                      <span className="text-xl font-bold text-red-600">₹{refundTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-3 px-5 py-4 border-t bg-slate-50">
              <button
                onClick={() => { setShowNewRefund(false); setSelectedInvoice(null); }}
                className="flex-1 py-2.5 border border-slate-200 rounded-lg font-medium text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleRefund}
                disabled={!selectedInvoice || processing || refundTotal === 0}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Create Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSRefundsPage;
